import { useAuth } from '@/lib/auth/auth';
import type { OrganizationInvitationPreview, OrgRole } from '@/lib/api/apiTypes';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { organizationKeys } from '@/features/workspaces/useOrganization';
import { organizationInvitationKeys } from '@/features/workspaces/useOrganizationInvitations';
import { organizationMemberKeys } from '@/features/workspaces/useOrganizationMembers';

type CreateOrganizationInvitationInput = {
  organizationID: string;
  email: string;
  role: Extract<OrgRole, 'manager' | 'social' | 'staff'>;
  venueIDs?: string[];
};

export function useCreateOrganizationInvitation() {
  const request = useAuth((state) => state.request);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organizationID, email, role, venueIDs }: CreateOrganizationInvitationInput) =>
      request<OrganizationInvitationPreview>(`/organizations/${organizationID}/invitations`, {
        method: 'POST',
        body: JSON.stringify({ email, role, venue_ids: venueIDs ?? [] }),
      }),
    onSuccess: async (_preview, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: organizationMemberKeys.list(variables.organizationID),
        }),
        queryClient.invalidateQueries({
          queryKey: organizationInvitationKeys.list(variables.organizationID),
        }),
        queryClient.invalidateQueries({
          queryKey: organizationKeys.detail(variables.organizationID),
        }),
      ]);
    },
  });
}
